import React, { useEffect, useMemo, useState } from 'react';
import { Form, Grid, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import BankCodeDropdown from '../../../components/BankCodeDropdown';
import { dangerNotification, successNotification } from '../../../services/notification';
import {
  activateStandaloneCharges,
  updateCommissionCollaboratorBankAccount,
} from '../../../services/standalone-charges';
import { ActivationFormState } from '../types';
import { initialActivationForm } from '../constants';
import {
  normalizePagarmeBankHolderName,
  PAGARME_BANK_HOLDER_NAME_MAX_LENGTH
} from '../utils/activationFormHelpers';
import { buildPagarmeActivationPayload } from '../utils/buildPagarmeActivationPayload';
import { mapActivationPayloadToForm } from '../utils/mapActivationPayloadToForm';
import { validateActivationStep } from '../utils/validateActivationStep';

type BankAccountSource = {
  recipientType?: ActivationFormState['recipientType'] | string;
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
  payload?: Record<string, unknown>;
};

const normalizeBankAccount = (bankAccount: Record<string, unknown> = {}) => ({
  holder_name: String(bankAccount.holder_name || '').trim().toUpperCase(),
  holder_type: String(bankAccount.holder_type || '').trim().toLowerCase(),
  holder_document: String(bankAccount.holder_document || '').replace(/\D/g, ''),
  bank: String(bankAccount.bank || '').trim(),
  branch_number: String(bankAccount.branch_number || '').trim(),
  branch_check_digit: String(bankAccount.branch_check_digit || '').trim(),
  account_number: String(bankAccount.account_number || '').trim(),
  account_check_digit: String(bankAccount.account_check_digit || '').trim(),
  type: String(bankAccount.type || '').trim().toLowerCase()
});

type Props = {
  open: boolean;
  mode: 'estate' | 'collaborator';
  estateId?: string;
  collaboratorId?: string;
  source: BankAccountSource | null;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

const EditBankAccountModal: React.FC<Props> = ({
  open,
  mode,
  estateId,
  collaboratorId,
  source,
  onClose,
  onSuccess
}) => {
  const [form, setForm] = useState<ActivationFormState>(initialActivationForm);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [biometryWarningOpen, setBiometryWarningOpen] = useState(mode === 'estate');

  const closeModal = () => {
    setBiometryWarningOpen(mode === 'estate');
    onClose();
  };

  useEffect(() => {
    if (!open || !source) return;
    const mapped = mapActivationPayloadToForm(
      {
        ...source,
        recipientType:
          source.recipientType === 'individual' ||
            source.recipientType === 'corporation'
            ? source.recipientType
            : undefined
      },
      initialActivationForm
    );
    setForm(mapped);
    setError(null);
    setBiometryWarningOpen(mode === 'estate');
  }, [open, source, mode]);

  const documentReadOnly = useMemo(
    () => form.bankHolderDocument || form.document,
    [form.bankHolderDocument, form.document]
  );

  const updateField = <K extends keyof ActivationFormState>(
    field: K,
    value: ActivationFormState[K]
  ) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'bankHolderName') {
        next.bankHolderName = normalizePagarmeBankHolderName(String(value || ''));
      }
      return next;
    });
  };

  const handleSave = async () => {
    const validationError = validateActivationStep(5, form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = buildPagarmeActivationPayload(form);
      if (mode === 'estate') {
        const storedBankAccount =
          source?.defaultBankAccount ||
          (source?.payload?.defaultBankAccount as Record<string, unknown> | undefined) ||
          (source?.payload?.default_bank_account as Record<string, unknown> | undefined);

        if (
          storedBankAccount &&
          JSON.stringify(normalizeBankAccount(storedBankAccount)) ===
            JSON.stringify(normalizeBankAccount(payload.defaultBankAccount))
        ) {
          closeModal();
          return;
        }

        await activateStandaloneCharges({
          ...payload,
          ...(estateId ? { estateId } : {})
        });
      } else {
        if (!collaboratorId) {
          throw new Error('Colaborador não informado.');
        }
        await updateCommissionCollaboratorBankAccount(collaboratorId, {
          defaultBankAccount: payload.defaultBankAccount
        });
      }

      successNotification(
        'Sucesso',
        mode === 'estate'
          ? 'Dados bancários atualizados. Conclua a nova biometria para liberar as cobranças.'
          : 'Conta bancária de recebimento atualizada com sucesso.'
      );
      await onSuccess?.();
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(message || 'Não foi possível atualizar a conta bancária.');
      dangerNotification(
        'Erro',
        message || 'Não foi possível atualizar a conta bancária.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Modal
      open={open && biometryWarningOpen}
      onClose={closeModal}
      size="small"
      closeIcon
    >
      <Modal.Header>Editar cadastro</Modal.Header>
      <Modal.Content>
        <p>
          Ao editar os dados cadastrados no módulo de cobranças será necessário
          realizar uma nova autenticação biométrica para validar as alterações.
        </p>
        <p>Deseja continuar?</p>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" text="Cancelar" light onClick={closeModal} />
        <Button
          type="button"
          text="Continuar"
          onClick={() => setBiometryWarningOpen(false)}
        />
      </Modal.Actions>
    </Modal>

    <Modal open={open && !biometryWarningOpen} onClose={closeModal} size="large" closeIcon>
      <Modal.Header>Alterar conta bancária</Modal.Header>
      <Modal.Content>
        <Message info>
          <p style={{ margin: 0 }}>
            {mode === 'estate'
              ? 'Se os dados bancários forem alterados, será necessário realizar uma nova biometria. Até a aprovação, não será possível criar ou gerir cobranças.'
              : 'Altera apenas a conta de recebimento Pagar.me.'}
          </p>
        </Message>
        {error && <Message negative>{error}</Message>}
        <Form>
          <Grid>
            <Grid.Row columns={3}>
              <Grid.Column>
                <Form.Field>
                  <label>Nome do titular</label>
                  <Input
                    maxLength={PAGARME_BANK_HOLDER_NAME_MAX_LENGTH}
                    value={form.bankHolderName}
                    onChange={(_, { value }) => updateField('bankHolderName', value)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Tipo do titular</label>
                  <Input
                    value={
                      form.bankHolderType === 'company'
                        ? 'Pessoa Jurídica'
                        : 'Pessoa Física'
                    }
                    readOnly
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Documento do titular</label>
                  <Input value={documentReadOnly} readOnly />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={5}>
              <Grid.Column>
                <Form.Field>
                  <label>Código do Banco</label>
                  <BankCodeDropdown
                    fluid
                    value={form.bankCode}
                    onChange={(_, { value }) =>
                      updateField('bankCode', String(value || ''))
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Agência</label>
                  <Input
                    value={form.bankBranchNumber}
                    onChange={(_, { value }) =>
                      updateField('bankBranchNumber', value.replace(/\D/g, ''))
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Dígito agência (opcional)</label>
                  <Input
                    value={form.bankBranchCheckDigit}
                    onChange={(_, { value }) =>
                      updateField('bankBranchCheckDigit', value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Conta</label>
                  <Input
                    value={form.bankAccountNumber}
                    onChange={(_, { value }) =>
                      updateField(
                        'bankAccountNumber',
                        value.replace(/\D/g, '').slice(0, 13)
                      )
                    }
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Dígito conta</label>
                  <Input
                    value={form.bankAccountCheckDigit}
                    onChange={(_, { value }) =>
                      updateField('bankAccountCheckDigit', value)
                    }
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" light onClick={closeModal} disabled={saving}>
          Cancelar
        </Button>
        <Button
          type="button"
          text="Salvar conta bancária"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        />
      </Modal.Actions>
    </Modal>
    </>
  );
};

export default EditBankAccountModal;
